import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Grid,
  Typography,
  Toolbar,
  Container,
  Paper,
  Avatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  List,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Badge
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Notifications as NotificationsIcon,
  Bluetooth as BluetoothIcon,
  HomeOutlined,
  ReceiptOutlined,
  PersonOutlined,
  LogoutOutlined,
  DescriptionOutlined,
  TableRestaurantOutlined
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Logout, reset } from '../../fitur/AuthSlice';
import axios from 'axios';
import LayoutKasir from './layoutKasir';
import ThermalPrinter from './ThermalPrinter';
import useSWR from 'swr';
import { apiHost } from '../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = async (url) => {
  const response = await axios.get(url, {
    withCredentials: true
  });
  return response.data;
};

export const ProfileKasir = ({ userUuid }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState('');
  const [nama, setNama] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentView, setCurrentView] = useState('profile');
  const [branchName, setBranchName] = useState("Nama Toko");
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerInstance, setPrinterInstance] = useState(null);
  const [notificationSnackbar, setNotificationSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const { data: notificationData } = useSWR(
    `${getApiBaseUrl()}/notifications`,
    fetcher,
    { 
      refreshInterval: 30000, 
      revalidateOnFocus: true,
      dedupingInterval: 5000
    }
  );
  const notificationCount = notificationData?.data?.length || 0;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/me`, {
          withCredentials: true
        });
        setProfile(response.data);
        setUsername(response.data.username || '');
        setNama(response.data.nama || '');
        setBranchName(response.data.cabang?.namacabang || "Nama Toko Tidak Diketahui");
      } catch (err) {
        setError('An error occurred while fetching profile');
        console.error("Gagal mendapatkan profile:", err);
      }
    };
    fetchProfile();
  }, [userUuid]);

  useEffect(() => {
    const fetchBranchName = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/me`, { withCredentials: true });
        setBranchName(response.data.cabang?.namacabang || "Nama Toko Tidak Diketahui");
      } catch (err) {
        console.error("Gagal mendapatkan nama cabang:", err);
        setBranchName("Nama Toko Tidak Diketahui");
      }
    };
    fetchBranchName();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/produkpercabang') {
      setCurrentView('home');
    } else if (path === '/transaksikasir') {
      setCurrentView('history');
    } else if (path === '/notification' || path === '/notification-kasir') {
      setCurrentView('notification');
    } else if (path === '/detail' || path === '/detail-kasir') {
      setCurrentView('detail');
    } else if (path === '/profilekasir') {
      setCurrentView('profile');
    }
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    try {
      dispatch(reset());
      dispatch(Logout());
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const handleNotificationClick = () => {
    navigate('/notification-kasir');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!userUuid) {
      setError('User UUID is missing. Please try again.');
      return;
    }

    if (!username.trim()) {
      setError('Username tidak boleh kosong.');
      return;
    }

    if (password && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const payload = { username, nama };
      if (password) {
        payload.password = password;
        payload.confpassword = confirmPassword;
      }
      const response = await axios.put(
        `${getApiBaseUrl()}/updateuser/me/${userUuid}`,
        payload,
        { withCredentials: true }
      );

      if (response.data) {
        setSuccess('Profil berhasil diupdate');
        setPassword('');
        setConfirmPassword('');
        setNotificationSnackbar({ open: true, message: 'Profil berhasil diupdate', severity: 'success' });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while updating profile');
      setNotificationSnackbar({ open: true, message: err.response?.data?.message || 'Gagal mengupdate profil', severity: 'error' });
    }
  };

  return (
    <LayoutKasir
      currentView={currentView}
      onViewChange={setCurrentView}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      showPrinterSettings={showPrinterSettings}
      onShowPrinterSettings={setShowPrinterSettings}
      printerConnected={printerConnected}
      printerInstance={printerInstance}
      onSetPrinterInstance={setPrinterInstance}
    >
      <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', pb: isMobile ? 8 : 0 }}>
      <ThermalPrinter 
        open={showPrinterSettings} 
        onClose={() => setShowPrinterSettings(false)}
        onPrinterConnected={(device) => {
          setPrinterInstance(device);
          setPrinterConnected(true);
        }}
        onPrinterDisconnected={() => {
          setPrinterInstance(null);
          setPrinterConnected(false);
        }}
        currentPrinter={printerInstance}
        connectionStatus={printerInstance?.getConnectionStatus?.()}
      />

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
          <BottomNavigation
            value={currentView}
            onChange={(event, newValue) => {
              if (newValue === 'home') {
                handleNavigate('/produkpercabang');
                setCurrentView('home');
              } else if (newValue === 'history') {
                handleNavigate('/transaksikasir');
                setCurrentView('history');
              } else if (newValue === 'notification') {
                handleNavigate('/notification-kasir');
                setCurrentView('notification');
              } 
              else if (newValue === 'layoutMeja') {
                handleNavigate('/layoutMeja');
                setCurrentView('layoutMeja');
              }
              
              else if (newValue === 'profile') {
                handleNavigate('/profilekasir');
                setCurrentView('profile');
              }
            }}
            showLabels
          >
            <BottomNavigationAction
              label="Home"
              value="home"
              icon={<HomeOutlined />}
            />
            <BottomNavigationAction
              label="Transaksi"
              value="history"
              icon={<ReceiptOutlined />}
            />
            <BottomNavigationAction
              label="Notifikasi"
              value="notification"
              icon={<NotificationsIcon />}
            />
            <BottomNavigationAction
              label="Profile"
              value="profile"
              icon={<PersonOutlined />}
            />
          </BottomNavigation>
        </Paper>
      )}

      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 3, 
          mb: 3,
          ml: 0,
          transition: 'margin 0.3s ease',
          width: '100%'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardHeader 
              subheader="User Profile" 
              title="My Profile"
              sx={{ bgcolor: '#f8f9fa', borderRadius: '16px 16px 0 0' }}
            />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              {profile ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Username</InputLabel>
                      <OutlinedInput
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        label="Username"
                        sx={{ borderRadius: '8px' }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Nama</InputLabel>
                      <OutlinedInput
                        value={nama}
                        onChange={(e) => setNama(e.target.value)}
                        label="Nama"
                        sx={{ borderRadius: '8px' }}
                      />
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                      Ditampilkan di struk & aplikasi, bukan username.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Role:</strong> {profile.role}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Cabang:</strong> {profile.cabang?.namacabang || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  Loading profile...
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>New Password</InputLabel>
                    <OutlinedInput
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      label="New Password"
                      autoComplete="new-password"
                      sx={{ borderRadius: '8px' }}
                    />
                  </FormControl>
                  <Typography variant="caption" color="text.secondary">
                    Kosongkan jika tidak ingin mengubah password.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Confirm Password</InputLabel>
                    <OutlinedInput
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      label="Confirm Password"
                      autoComplete="new-password"
                      sx={{ borderRadius: '8px' }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: '8px' }}>
                  {success}
                </Alert>
              )}
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              <Button 
                type="submit" 
                variant="contained"
                sx={{
                  bgcolor: '#337ab7',
                  '&:hover': {
                    bgcolor: '#286090',
                  },
                  borderRadius: '12px',
                  px: 3
                }}
              >
                Simpan Profil
              </Button>
            </CardActions>
          </Card>
        </form>
      </Container>

      <Snackbar
        open={notificationSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setNotificationSnackbar({ ...notificationSnackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotificationSnackbar({ ...notificationSnackbar, open: false })}
          severity={notificationSnackbar.severity}
          variant="filled"
          sx={{ borderRadius: '12px' }}
        >
          {notificationSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </LayoutKasir>
  );
};
